import Key from "csi/core/Key";
import SpecBuilder from "csi/core/SpecBuilder";
import html from "csi/html";
import types from "csi/types";
import * as _ from "lodash";
import {
	CANVAS_EDITOR_ROOT,
	CANVAS_PADDING,
	DEFAULT_CANVAS_MODE,
	MARGIN_10,
	MOVEMENT_THRESHOLD_PX,
	OVERFLOW,
	PERCENTAGE_HUNDRED,
	PIXELS, PLACEHOLDER_MULTILINE_REGEX, PLACEHOLDER_VALUE_SPAN, RIGHT_SIDE_MENU_WIDTH, SVG_CANVAS, ZERO_TEXT
} from "../Constants";
import {
	IRectSizeBox,
	removePlaceholdersMatchingKeys,
	restoreDefaultPlaceholderView,
	unlinkAllPlaceholders
} from "../store/reducers/util";
import { IGroupShape } from "../widgets/interfaces/model/PageObjects";
import { IShape } from "../widgets/interfaces/model/PageObjects/Shape";
import { PageObjectType } from "../widgets/interfaces/model/Types";
import { CanvasMode, FillTypes, IPageObjectTypes } from "../widgets/interfaces/model/enumerations/index";
import { ICanvasEditorState, IPage } from "../widgets/interfaces/model/index";
import { IPictureFillProperties } from "../widgets/interfaces/model/properties/FillProperties";
import { IPosition } from "../widgets/meta/Drag";
import { getCanvasMode } from "../widgets/selectors/canvasMode";
import { getZoomValue } from "../widgets/selectors/index";
import {
	getPlaceholderRichTextElement,
	resetPlaceholderGroup,
	setupPlaceholderHTML,
	togglePlaceholderLabelOptionally,
} from "../widgets/util";
import { getStore } from "./ReduxRegistry";
import { IAddImageRequest } from "./reducers/PageOperations";
import { addAttributeToObject, generateUUID } from "./reducers/util";

/**
 * Page placeholder object data interface
 */
export interface IPagePlaceholderObjectData {
	/**
	 * Placeholder key.
	 */
	attributeKey?: string;

	/**
	 * Image placeholder flag
	 */
	imagePlaceholder?: boolean;

	/**
	 * Url for the node associated with the placeholder value.
	 */
	nodeURL?: string;

	/**
	 * If the shape was not allowed to be filled, this flag will be set.
	 */
	noPermissionToFill?: boolean;

	/**
	 * Data value for attribute placeholder.
	 */
	value?: string | undefined;
};

/**
 * Page placeholder difference and data object
 */
export interface IPlaceholderDifferenceData {
	/**
	 * Placeholder key.
	 */
	attributeKey: string;

	/**
	 * Format (Rich Text, Image, etc)
	 */
	format: string;

	/**
	 * Original placeholder data value.
	 */
	originalValue: string;

	/**
	 * Latest placeholder data value.
	 */
	latestValue: string;

	/**
	 * URL for the node populating the data for the placeholder.
	 */
	url: string;

	/**
	 * Placeholder type (ArtworkRevision, Colorway, etc).
	 */
	type: string;

	/**
	 * Flag indicating if placeholder value changed.
	 */
	changed: boolean;

	/**
	 * Name of the populating type.
	 */
	name: string;

	/**
	 * Whether the currently filling user has access to the result.
	 */
	noCurrentAccess: boolean;

	/**
	 * Whether the user who originally filled the placeholder had access to the result.
	 */
	noPermissionToFill: boolean;

	/**
	 * Flag indicating image placeholder.
	 */
	isImage: boolean;

	/**
	 * Flag indicating if attribute should be deleted.
	 */
	deletePlaceholder: boolean;

	/**
	 * Data on image such as dimensions.
	 */
	imageObj: IAddImageRequest;

	/**
	 * Shape id of placeholder group containing attribute.
	 */
	groupShapeId: string;

	/**
	 * Canvas page index of placeholder group containing attribute.
	 */
	pageIndex: number;

	/**
	 * Flag indicating if should restore placeholder to default image display or default labeled image display.
	 */
	restoreToDefaultView?: boolean;

	/**
	 * Flag indicating whether or not to remove the URL associated with the placeholder
	 */
	saveURL: boolean;
}

/**
 * Return map of placeholder keys for objects having attributeKey property.
 * @param pageModel contains data for canvas page objects.
 * @return contains map of placeholder keys to attribute keys for page objects.
 */
const _extractPagePlaceholderKeys = (pageModel: { pageObjects: [{ attributeKey: string }] }): any => {
	const map: { [key: string]: string } = {};
	let ctr: number = 0;

	if (pageModel && pageModel.pageObjects) {
		// First get all actual page objects, including objects in groups and sub-groups.
		let allPageObjects: any[] = [];

		pageModel.pageObjects.forEach((pageObject: any) => {
			allPageObjects = allPageObjects.concat(_getPageObjects(pageObject));
		});

		// Generate map of placeholder keys and their attribute keys.
		_.each(allPageObjects, (pageObject: { attributeKey: string }) => {
			const key: string = pageObject.attributeKey;

			if (key) {
				let index = map[key];
				if (index === undefined) {
					index = map[key] = String(ctr);
					ctr++;
				}

				pageObject.attributeKey = index;
			}
		});
	}

	return _.isEmpty(map) ? null : _.invert(map);
};

/**
 * Return page model with keys removed
 * @param pageModel contains data for canvas page objects.
 * @return page model with keys removed
 */
const _removeImpossibleKeys = (pageObjects: PageObjectType[], keys: string[]): PageObjectType[] => {
	let newObjects = _.cloneDeep(pageObjects);

	newObjects = removePlaceholdersMatchingKeys(pageObjects, keys);

	return newObjects;
};

/**
 * Get placeholders recursively from page objects (in case of nested groups)
 * @param pageObjects - all page objects on current page
 * @param placeholders - placeholders array to return
 */
const _getPagePlaceholders = (pageObjects: PageObjectType[], placeholders: PageObjectType[]): PageObjectType[] => {
	pageObjects.forEach((pageObject: any) => {
		if (pageObject.isAttributePlaceholder) {
			placeholders.push(pageObject);
		} else if (pageObject.childObjects) {
			_getPagePlaceholders(pageObject.childObjects, placeholders);
		}
	});
	return placeholders;
};

/**
 * Get valid page objects
 */
const getValidPageObjects = (pageObjects: PageObjectType[], parentObject?: IGroupShape): PageObjectType[] => {
	// filter attribute placeholders with no child objects ( C8-48894 ) -------------------
	let i = pageObjects.length;
	while (i--) {
		const pageObject = pageObjects[i] as IGroupShape;
		if (pageObject.childObjects && !pageObject.isAttributePlaceholder) {
			getValidPageObjects(pageObject.childObjects, pageObject);
		}
		if (pageObject.childObjects && !pageObject.childObjects.length) {
			if (parentObject) {
				parentObject.childObjects.splice(i, 1);
			} else {
				pageObjects.splice(i, 1);
			}
		}
	}
	// ... TODO - other filtering of bad data before saving canvas can go here
	return pageObjects;
};

/**
 * Update all page objects.
 * @param index canvas page index for which to perform update
 * @param pageObjects contains canvas page objects
 * @param placeholderDifferenceData contains data for canvas page objects
 * @param placeholderDeletions contains data for placeholders to delete
 * @param bAccessible whether to fill newly accessible placeholders instead of changed ones
 * @return updated page objects
 */
const _updatePageObjects = (
	index: number,
	pageObjects: PageObjectType[],
	placeholderDifferenceData: [IPlaceholderDifferenceData],
	placeholderDeletions: [string],
	bAccessible?: boolean,
): PageObjectType[] => {
	const updatedObjects = _.cloneDeep(pageObjects);

	// First perform deletions.
	if (placeholderDeletions && placeholderDeletions.length && !bAccessible) {
		const _recurse = (baseObjects: PageObjectType[], parentObject?: IGroupShape): void => {
			let i = baseObjects.length;
			while (i--) {
				const pageObject = baseObjects[i] as IGroupShape;
				if (pageObject.childObjects && !pageObject.isAttributePlaceholder) {
					_recurse(pageObject.childObjects, pageObject);
				} else if (pageObject.childObjects && pageObject.isAttributePlaceholder) {
					let j = pageObject.childObjects.length;
					while (j--) {
						const child = pageObject.childObjects[j] as IShape;
						if (_.indexOf(placeholderDeletions, child.attributeKey) !== -1) {
							pageObject.childObjects.splice(j, 1);
						}
					}
				}
				if (pageObject.childObjects && !pageObject.childObjects.length) {
					if (parentObject) {
						parentObject.childObjects.splice(i, 1);
					} else {
						baseObjects.splice(i, 1);
					}
				}
			}
		};
		_recurse(updatedObjects);
	}

	// get all placeholders from page
	const pagePlaceholders = _getPagePlaceholders(updatedObjects, []);
	if (pagePlaceholders && placeholderDifferenceData && placeholderDifferenceData.length) {
		// Go through page objects and update their values if they have changed.
		_.each(pagePlaceholders, (groupObject: IGroupShape) => {
			// Page objects are group objects so need to find the child that changed.
			const children = groupObject.childObjects;
			_.each(children, (child) => {
				_.each(placeholderDifferenceData, (placeholderDifference) => {
					child = child as IShape;
					const usePlaceholder = (placeholderDifference.changed && !bAccessible) ||
						(bAccessible && placeholderDifference.noPermissionToFill &&
							!placeholderDifference.noCurrentAccess);
					if (
						usePlaceholder &&
						placeholderDifference.pageIndex === index &&
						placeholderDifference.groupShapeId === groupObject.shapeId &&
						placeholderDifference.attributeKey === child.attributeKey
					) {
						// Restore default view if no longer have url to image.
						if (placeholderDifference.restoreToDefaultView) {
							restoreDefaultPlaceholderView(child, placeholderDifference.saveURL);
						} else if (child.isTextShape ||
							(child.imagePlaceholder && !child.isPicture && placeholderDifference.noCurrentAccess)) {
							addAttributeToObject(
								child,
								{
									format: placeholderDifference.format,
									text: placeholderDifference.latestValue,
									permission: true,
									restoreImagePlaceholder: false,
								},
							);
						} else if (child.isPicture || (child.imagePlaceholder && placeholderDifference.imageObj)) {
							const imagePlaceholderObject = _makeImageFromPlaceholder(
								child,
								placeholderDifference.imageObj,
							);
							child.isPicture = imagePlaceholderObject.isPicture;
							child.nodeURL = imagePlaceholderObject.nodeURL;
							child.spPr = imagePlaceholderObject.spPr;
							delete child.imagePlaceholder;
							delete child.txBody;
						}
					}
				});
			});
			// object was a group shape, reset cy
			resetPlaceholderGroup(groupObject);
		});
	}
	return updatedObjects;
};

/**
 * Return page model with all attribute keys removed
 * @param pageObjects contains data for canvas page objects.
 * @return page model with all placeholders unlinked
 */
const _unlinkAllAttributePlaceholders = (pageObjects: PageObjectType[]): PageObjectType[] => {
	return unlinkAllPlaceholders(pageObjects);
};

/**
 * Get canvas container scroll position
 * @return {IPosition} scroll - canvas scroll position
 */
export const getCanvasContainerScrollPosition = (): IPosition => {
	const state = getStore()
		.getState() as ICanvasEditorState;
	const { parentElement } = state.canvasEditor.canvasContainerEl;
	const scroll: IPosition = {
		x: parentElement.scrollLeft,
		y: parentElement.scrollTop,
	};
	return scroll;
};

/**
 * Set canvas container to a specific scroll position
 * @param {IPosition} position - position to scroll to
 */
export const setCanvasContainerScrollPosition = (position: IPosition): void => {
	const state = getStore()
		.getState() as ICanvasEditorState;
	const { parentElement } = state.canvasEditor.canvasContainerEl;
	parentElement.scrollLeft = position.x || 0;
	parentElement.scrollTop = position.y || 0;
};

/**
 * Return whether or not canvas mode is currently the default.
 * @return canvas container node
 */
export const getCanvasContainerEl = (): HTMLElement => {
	const state = getStore()
		.getState() as ICanvasEditorState;
	return state.canvasEditor.canvasContainerEl;
};

/**
 * Get all actual page objects, including objects in groups and sub-groups.
 * @param pageObject contains data for a canvas page object (which might have sub-objects).
 * @param allPageObjects container for page objects and sub-page objects
 * @return page objects and sub-page objects
 */
const _getPageObjects = (pageObject: PageObjectType, allPageObjects: PageObjectType[] = []): PageObjectType[] => {
	if (pageObject.hasOwnProperty("attributeKey")) {
		allPageObjects.push(pageObject);
		return allPageObjects;
	} else if (pageObject.hasOwnProperty("childObjects")) {
		pageObject = pageObject as IGroupShape;
		pageObject.childObjects.forEach((child: any) => {
			return _getPageObjects(child, allPageObjects);
		});
	}
	return allPageObjects;
};

/**
 * Get placeholder text format value
 */
export const getPlaceholderTextFormat = () => {
	const state = getStore()
		.getState() as ICanvasEditorState;
	return state.canvasEditor.placeholderTextFormat;
};

/**
 * Return dimensions of absolutely positioned items in canvas
 * @return dimensions
 */
const _getEditorDimensions = (): IRectSizeBox | undefined => {
	const canvasContainerEl = getCanvasContainerEl();
	if (!canvasContainerEl) {
		return undefined;
	}

	// iterate over the elements in canvas + notes to get the editor bounding box
	const canvasPage: HTMLElement = canvasContainerEl.querySelector(".csi-canvas-page");
	const canvasPageBound: DOMRect = canvasPage.getBoundingClientRect();
	const canvasRoot: HTMLElement = canvasPage.querySelector(CANVAS_EDITOR_ROOT);
	const canvasNoteList: HTMLElement = canvasPage.querySelector(".csi-container-list");

	// set top/right/bottom/left initially from canvasRoot bounds because we fall back on these
	let { top, right, bottom, left } = canvasPageBound;
	_.each([...canvasRoot.children, ...canvasNoteList.children], (child) => {
		const bound = child.getBoundingClientRect();
		// When an old canvas is deleted, the canvas root node is present but hidden until
		// a new one is created.  In this case we do not need to include its children on the screen.
		if (bound.width > 0 || bound.height > 0) {
			// min of top and left, max of right and bottom
			top = Math.min(top, bound.top);
			// add an insignicant amount here, since the origin is top left, so spacing is added to right and bottom
			right = Math.max(right + 0.001, bound.right);
			bottom = Math.max(bottom + 0.001, bound.bottom);
			left = Math.min(left, bound.left);
		}
	});
	// return a client rect
	const dimensions: IRectSizeBox = {
		top,
		right,
		bottom,
		left,
		height: bottom - top,
		width: right - left,
	};
	return dimensions;
};

/**
 * Expand editor according to dimensions given by rect
 * @param rect contains rectangle of dimensions for which to expand editor with
 * @param resize flag for whether or not to reset left and top padding
 * @param editMode flag indicating whether or not canvas is in edit mode when expanding
 * @param scale incoming scale
 * @param prevScroll previous scroll position
 */
const _expandEditor = (rect: IRectSizeBox | undefined, resize: boolean, editMode: boolean, scale?: number, prevScroll?: IPosition): void => {
	const canvasContainerEl = getCanvasContainerEl();

	// if there is no content rectangle, no canvas container, or we are not in editMode and have no scale, return
	if (!rect || !canvasContainerEl || (!editMode && !scale)) {
		return;
	} else if (!scale) {
		scale = getZoomValue(1) || 1;
	} else {
		scale = 1 / scale;
	}

	// get all necessary bound information and store in variables
	const { style } = canvasContainerEl;
	const scrollNode = canvasContainerEl.parentElement as HTMLElement;
	const scrollNodeBound = scrollNode.getBoundingClientRect();
	const canvasPage = canvasContainerEl.querySelector(".csi-canvas-page") as HTMLElement;
	const canvasPageBound = canvasPage.getBoundingClientRect();
	const blankPage = canvasContainerEl.querySelector(".csi-canvas-blank-container") as HTMLElement;
	const blankPageBound = blankPage.getBoundingClientRect();
	const svgCanvas = canvasPage.querySelector(`.${SVG_CANVAS}`) as HTMLElement;

	// update actual padding with scale
	const PADDING = CANVAS_PADDING / scale;

	// set canvas container height and with to match pageNode (we'll be using padding to expand the editor)
	style.width = (scale * (canvasPageBound.width || blankPageBound.width)) + PIXELS;
	style.height = (scale * (canvasPageBound.height || blankPageBound.height)) + PIXELS;

	// handle right side menu - either preload or use available width depending on whether the toolbar node exists and has width
	const toolbarRight = document.querySelector(".csi-canvas-right-menu") as HTMLElement;
	const toolbarRightBound = toolbarRight?.getBoundingClientRect().width ?? 0;
	const preloadToolbar = !toolbarRightBound && !blankPageBound.width && editMode;
	// center using left padding, this used to be done by a transform in C8 CanvasWidget.js
	let offsetToCenterLeft;
	if (preloadToolbar) {
		const width = Math.max(0, scrollNodeBound.width - RIGHT_SIDE_MENU_WIDTH);
		offsetToCenterLeft = Math.max(0, scale * (width - canvasPageBound.width) / 2.0);
	} else {
		offsetToCenterLeft = Math.max(0, scale / 2.0 * (scrollNodeBound.width - (canvasPageBound.width || blankPageBound.width)));
	}

	// because the entire canvas container is being scaled with top left as the origin,
	// margin negative takes away extra space on bottom
	const setMarginBottom = () => {
		style.marginBottom = (scale > 1) ? (-(1 - 1 / scale) * canvasContainerEl.offsetHeight) + PIXELS : "";
	};

	// set overflow of x and y depending on the revised rectangle boolean values
	const setOverflow = () => {
		const { x, y } = overflow;
		const { style } = scrollNode;
		const { AUTO, HIDDEN } = OVERFLOW;
		if (x && y) {
			style.overflow = AUTO;
		} else if (x && !y) {
			style.overflowX = AUTO;
			style.overflowY = HIDDEN;
		} else if (!x && y) {
			style.overflowX = HIDDEN;
			style.overflowY = AUTO;
		} else {
			style.overflow = HIDDEN;
		}
	};

	const overflow = {
		x: (rect.left < scrollNodeBound.left || rect.right > scrollNodeBound.right),
		y: (rect.top < scrollNodeBound.top || rect.bottom > scrollNodeBound.bottom),
	};

	// in the revised content rectangle, only use padding if there is overflow in bound
	const revisedRect = {
		bottom: (rect.bottom - scrollNodeBound.bottom > MOVEMENT_THRESHOLD_PX) ? rect.bottom + PADDING : rect.bottom,
		left: (scrollNodeBound.left - rect.left > MOVEMENT_THRESHOLD_PX) ? rect.left - PADDING : rect.left,
		right: (rect.right - scrollNodeBound.right > MOVEMENT_THRESHOLD_PX) ? rect.right + PADDING : rect.right,
		top: (scrollNodeBound.top - rect.top > MOVEMENT_THRESHOLD_PX) ? rect.top - PADDING : rect.top,
	};


	// if we aren't in edit mode yet, there is special handling.
	// we don't want scrolling to be enabled here beyond the white space.
	if (!editMode) {
		// hide the overflow when not in edit mode
		svgCanvas.style.overflow = "hidden";

		style.paddingTop = MARGIN_10 + PIXELS;
		style.paddingLeft = (Math.max(MARGIN_10, offsetToCenterLeft)) + PIXELS;
		style.paddingBottom = 0 + PIXELS;
		style.paddingRight = (Math.max(MARGIN_10, offsetToCenterLeft)) + PIXELS;

		setMarginBottom();

		setOverflow();

		// restore previous scroll position if exists, otherwise reset it
		if (prevScroll) {
			setCanvasContainerScrollPosition(prevScroll);
		} else {
			scrollNode.scrollLeft = scrollNode.scrollTop = 0;
		}
		return;
	} else {
		svgCanvas.style.overflow = "";
	}

	// delta values left and top with padding
	const deltaLeft = Math.max(0, scale * (canvasPageBound.left - revisedRect.left));
	const deltaTop = Math.max(0, scale * (canvasPageBound.top - revisedRect.top));

	// read initial left and top padding in
	const paddingLeft = parseFloat(style.paddingLeft || ZERO_TEXT);
	const paddingTop = parseFloat(style.paddingTop || ZERO_TEXT);

	// this is the padding used only for resize purposes
	const resizePadding = {
		bottom: Math.max(
			MARGIN_10, scale * (revisedRect.bottom - canvasPageBound.bottom),
			scale * (scrollNodeBound.height - canvasPageBound.height - MARGIN_10),
		),
		right: Math.max(offsetToCenterLeft, scale * (revisedRect.right - canvasPageBound.right)),
	};

	// if we are resizing from C8, the width and height can be reset
	if (resize) {
		// reset scroll position
		scrollNode.scrollLeft = scrollNode.scrollTop = 0;

		// reset padding to accommodate only for content and padding used to center canvas
		const paddingTop = ((deltaTop > 25) ? deltaTop : MARGIN_10);
		style.paddingTop = paddingTop + PIXELS;
		style.paddingLeft = Math.max(MARGIN_10, offsetToCenterLeft, deltaLeft) + PIXELS;

		// account for scrollbars when setting the padding
		style.paddingRight = resizePadding.right + PIXELS;
		style.paddingBottom = resizePadding.bottom + PIXELS;

		// set scroll variables because the view must be scrolled back into position when adding space to top or left
		const scrollX = (deltaLeft - offsetToCenterLeft) / scale;
		const scrollY = (paddingTop - MARGIN_10) / scale;

		// if padding used to accommodate content was more than what was used for centering, scroll back to center
		if (deltaLeft > offsetToCenterLeft && deltaTop > 0) {
			scrollNode.scrollBy(scrollX, scrollY);
		} else if (deltaLeft > offsetToCenterLeft) {
			scrollNode.scrollBy(scrollX, 0);
		} else if (deltaTop > 0) {
			scrollNode.scrollBy(0, scrollY);
		}

		// restore previous scroll position if exists
		if (prevScroll) {
			setCanvasContainerScrollPosition(prevScroll);
		}
	} else {
		// if delta is more than previous padding values, add space then scroll back to previous position
		const prevHeight = canvasContainerEl.offsetHeight;
		const prevWidth = canvasContainerEl.offsetWidth;
		if (deltaLeft > paddingLeft && deltaTop > paddingTop) {
			style.paddingLeft = deltaLeft + PIXELS;
			style.paddingTop = deltaTop + PIXELS;
			const scrollX = (canvasContainerEl.offsetWidth - prevWidth) / scale;
			const scrollY = (canvasContainerEl.offsetHeight - prevHeight) / scale;
			scrollNode.scrollBy(scrollX, scrollY);
		} else if (deltaLeft > paddingLeft) {
			style.paddingLeft = deltaLeft + PIXELS;
			const scrollX = (canvasContainerEl.offsetWidth - prevWidth) / scale;
			scrollNode.scrollBy(scrollX, 0);
		} else if (deltaTop > paddingTop) {
			style.paddingTop = deltaTop + PIXELS;
			const scrollY = (canvasContainerEl.offsetHeight - prevHeight) / scale;
			scrollNode.scrollBy(0, scrollY);
		}

		// parse previous padding and get new delta padding for content
		const deltaBottom = (scale * Math.max(0, revisedRect.bottom - canvasPageBound.bottom));
		const parsePadding = {
			deltaBottom: overflow.y ? deltaBottom : deltaBottom - MARGIN_10,
			deltaRight: scale * Math.max(0, revisedRect.right - canvasPageBound.right),
			prevBottom: parseFloat(style.paddingBottom || ZERO_TEXT),
			prevRight: parseFloat(style.paddingRight || ZERO_TEXT),
		};

		if (parsePadding.deltaRight > parsePadding.prevRight) {
			style.paddingRight = parsePadding.deltaRight + PIXELS;
		}

		if (parsePadding.deltaBottom > parsePadding.prevBottom) {
			style.paddingBottom = parsePadding.deltaBottom + PIXELS;
		}
	}

	setMarginBottom();

	setOverflow();
};

/**
 * Get all page attribute placeholder objects, including objects in groups and sub-groups.
 * @param pageObject contains data for a canvas page object (which might have sub-objects).
 * @param allPagePlaceholderObjects container for page objects and sub-page objects that are placeholders
 * @return page objects and sub-page objects that are placeholders
 */
export const getPageAttributePlaceholderObjects = (
	pageObject: IGroupShape,
	allPagePlaceholderObjects: IGroupShape[] = [],
): IGroupShape[] => {
	const po = pageObject as any;
	if (po.attributeKey !== undefined && po.attributeKey !== null) {
		allPagePlaceholderObjects.push(pageObject);
		return allPagePlaceholderObjects;
	} else if (pageObject.hasOwnProperty("childObjects")) {
		pageObject.childObjects.forEach((child: any) => {
			return getPageAttributePlaceholderObjects(child, allPagePlaceholderObjects);
		});
	}
	return allPagePlaceholderObjects;
};

/**
 * Get all page placeholder objects, including objects in groups and sub-groups.
 * @param pageObject contains data for a canvas page object (which might have sub-objects).
 * @param allPagePlaceholderObjects container for page objects and sub-page objects that are placeholders
 * @return page objects and sub-page objects that are placeholders
 */
export const getPagePlaceholderObjects = (
	pageObject: IGroupShape,
	allPagePlaceholderObjects: IGroupShape[] = [],
): IGroupShape[] => {
	if (pageObject.hasOwnProperty("attributeKey") || pageObject.imagePlaceholder) {
		allPagePlaceholderObjects.push(pageObject);
		return allPagePlaceholderObjects;
	} else if (pageObject.hasOwnProperty("childObjects")) {
		pageObject.childObjects.forEach((child: any) => {
			return getPagePlaceholderObjects(child, allPagePlaceholderObjects);
		});
	}
	return allPagePlaceholderObjects;
};

/**
 * Get all page image placeholder objects, including objects in groups and sub-groups.
 * @param pageObject contains data for a canvas page object (which might have sub-objects).
 * @param allPageImagePlaceholderObjects container for page objects and sub-page objects that are image placeholders
 * @return page objects and sub-page objects that are image placeholders
 */
export const getPageFromImagePlaceholderObjects = (
	pageObject: any,
	allPageImagePlaceholderObjects: IGroupShape[] = [],
): any => {
	if (pageObject.imagePlaceholder || pageObject.isPictureFromPlaceholder) {
		allPageImagePlaceholderObjects.push(pageObject);
		return allPageImagePlaceholderObjects;
	} else if (pageObject.hasOwnProperty("childObjects")) {
		pageObject.childObjects.forEach((child: any) => {
			return getPageFromImagePlaceholderObjects(child, allPageImagePlaceholderObjects);
		});
	}
	return allPageImagePlaceholderObjects;
};

/**
 * Get all page image placeholder objects, including objects in groups and sub-groups.
 * @param pageObject contains data for a canvas page object (which might have sub-objects).
 * @param allPageImagePlaceholderObjects container for page objects and sub-page objects that are image placeholders
 * @return page objects and sub-page objects that are image placeholders
 */
export const getPageImagePlaceholderObjects = (
	pageObject: any,
	allPageImagePlaceholderObjects: Object[] = [],
): any => {
	if (pageObject.imagePlaceholder) {
		allPageImagePlaceholderObjects.push(pageObject);
		return allPageImagePlaceholderObjects;
	} else if (pageObject.hasOwnProperty("childObjects")) {
		pageObject.childObjects.forEach((child: any) => {
			return getPageImagePlaceholderObjects(child, allPageImagePlaceholderObjects);
		});
	}
	return allPageImagePlaceholderObjects;
};

/** get number of images from page */
export function getImageCount(page: IPage) {
	const { pageObjects } = page;
	let count = 0;
	if (pageObjects.length) {
		function _recursive(pageObjects) {
			for (const pageObject of pageObjects) {
				if (pageObject.childObjects) {
					_recursive(pageObject.childObjects);
				}
				if (pageObject.isPicture) {
					count++;
				}
			}
		}
		_recursive(pageObjects);
	}
	return count;
}

/**
 * Setup placeholders on page objects for use after stories in C8 invalidate old canvas data.
 * This function is an alternative to migration that alters the data in real time whenever the user loads canvas.
 * This setup consists of changing the HTML structure of the each placeholder.
 * If coming from a Canvas Template, adjust the HTML structure to match autospace setting.
 * Stories that this function covers should be added below.
 * Placeholder split into 4 spans 7.2 ( C8-16890 ).
 * Hide / unhide placeholder labels that don't have values ( C8-49701 ).
 */
export const setupPageContentPlaceholderHTML = (pageJSON: string, fromCanvasTemplate: boolean): string => {
	// function to convert the html recursively
	const _recursiveHTML = (pageObjects) => {
		for (const pageObject of pageObjects) {
			if (fromCanvasTemplate && pageObject.autospace) {
				resetPlaceholderGroup(pageObject);
			}
			if (pageObject.childObjects) {
				_recursiveHTML(pageObject.childObjects);
			}
			if (pageObject.attributeKey && pageObject.isTextShape) {
				setupPlaceholderHTML(pageObject);
				togglePlaceholderLabelOptionally(pageObject, false);
			}
		};
	};

	const pageModel = pageJSON.length ? JSON.parse(pageJSON) : { pageObjects: [] };
	_recursiveHTML(pageModel.pageObjects);
	return JSON.stringify(pageModel);
};

/**
 * Toggle placeholder labels optionally based on edit mode and value content.
 * This function is for returning a JSON string to the client so the redux store can be bypassed.
 */
export const setupPlaceholderLabelOptionally = (pageJSON: string, editMode: boolean): string => {
	// function to convert the html recursively
	const _recursiveHTML = (pageObjects) => {
		for (const pageObject of pageObjects) {
			if (pageObject.childObjects) {
				_recursiveHTML(pageObject.childObjects);
			}
			if (pageObject.attributeKey && pageObject.isTextShape) {
				togglePlaceholderLabelOptionally(pageObject, editMode);
			}
			if (pageObject.autospace) {
				resetPlaceholderGroup(pageObject);
			}
		};
	};

	const pageModel = pageJSON.length ? JSON.parse(pageJSON) : { pageObjects: [] };
	_recursiveHTML(pageModel.pageObjects);
	return JSON.stringify(pageModel);
};

/**
 * Replace placeholder keys with corresponding attribute key strings.
 * @param pageModel contains data for canvas page objects.
 * @param keys contains map of placeholder keys to attribute keys for page objects.
 */
export const setPagePlaceholderKeys = (
	pageModel: { pageObjects: [IShape] },
	keys: { [key: string]: string },
): string => {
	if (!pageModel || !pageModel.pageObjects.length) {
		return;
	}

	// replace keys recursively
	const _recursiveReplaceKeys = (pageObjects) => {
		for (const pageObject of pageObjects) {
			if (pageObject.childObjects) {
				_recursiveReplaceKeys(pageObject.childObjects);
			}
			if (pageObject.attributeKey) {
				const int = parseInt(pageObject.attributeKey);
				if (int >= 0) {
					pageObject.attributeKey = keys[int];
				}
			}
		}
	};

	_recursiveReplaceKeys(pageModel.pageObjects);
	return JSON.stringify(pageModel);
};

/**
 * Return whether or not canvas is currently in edit mode.
 */
const isDefaultCanvasEditMode = (): boolean => {
	const state: any = getStore()
		.getState();
	return state.canvasEditor.isEditEnabled;
};

/**
 * Return whether or not canvas mode is currently the default.
 */
const _isDefaultCanvasMode = (): boolean => {
	const state: any = getStore()
		.getState();
	const canvasMode = getCanvasMode(state);
	return canvasMode === DEFAULT_CANVAS_MODE;
};

/**
 * Return whether or not canvas mode is in dragging type instances mode.
 */
const isDragTypeInstanceMode = (): boolean => {
	const state: any = getStore()
		.getState();
	return getCanvasMode(state) === CanvasMode.DRAG_TYPE_INSTANCE;
};

/**
 * Return whether or not canvas mode is currently DRAW.
 */
const _isDrawCanvasMode = (): boolean => {
	const state: any = getStore()
		.getState();
	const canvasMode = getCanvasMode(state);
	return canvasMode === CanvasMode.DRAW;
};

/**
 * Return whether or not canvas mode is currently EDIT_CURVE.
 */
const _isEditCurveCanvasMode = (): boolean => {
	const state: any = getStore()
		.getState();
	const canvasMode = getCanvasMode(state);
	return canvasMode === CanvasMode.EDIT_CURVE || canvasMode === CanvasMode.EDIT_PEN;
};

const _isEditPenMode = (): boolean => {
	const state: any = getStore()
		.getState();
	const canvasMode = getCanvasMode(state);
	return canvasMode === CanvasMode.EDIT_PEN;
};

/**
 * Return whether or not canvas mode is currently EDIT_PICTURE.
 */
const _isEditPictureCanvasMode = (): boolean => {
	const state: any = getStore()
		.getState();
	const canvasMode = getCanvasMode(state);
	return canvasMode === CanvasMode.EDIT_PICTURE;
};

/**
 * Return whether or not canvas mode is currently EDIT_TEXT.
 */
const _isEditTextCanvasMode = (): boolean => {
	const state: any = getStore()
		.getState();
	const canvasMode = getCanvasMode(state);
	return canvasMode === CanvasMode.EDIT_TEXT;
};

/**
 * Return whether or not canvas mode is currently NODRAW.
 */
const _isNoDrawCanvasMode = (): boolean => {
	const state: any = getStore()
		.getState();
	const canvasMode = getCanvasMode(state);
	return canvasMode === CanvasMode.NODRAW;
};

/**
 * Return whether or not canvas mode is currently SELECT.
 */
const _isSelectCanvasMode = (): boolean => {
	const state: any = getStore()
		.getState();
	const canvasMode = getCanvasMode(state);
	return canvasMode === CanvasMode.SELECT;
};

/**
 * Return current canvas mode.
 */
const _getCanvasMode = (): CanvasMode => {
	const state: any = getStore()
		.getState();
	return getCanvasMode(state);
};

/**
 * Return default canvas mode.
 */
const _getDefaultCanvasMode = (): CanvasMode => {
	return DEFAULT_CANVAS_MODE;
};

/**
 * Return data for all placeholder objects on a canvas page, organized by the group that contains them.
 * @param {IPage} pageData - optional page data from which to get data, if absent get from current page
 */
const _getAllPagePlaceholderObjectData = (pageData: IPage): { [groupShapeId: string]: IPagePlaceholderObjectData[] } => {
	// Get all placeholder objects on the page, organized by their containing group shape id.
	const allPagePlaceholderObjects: any = _getAllPagePlaceholderObjects(pageData);
	const allPagePlaceholderObjectData: { [groupShapeId: string]: IPagePlaceholderObjectData[] } = {};
	const specBuilder = new SpecBuilder();

	_.each(allPagePlaceholderObjects, (placeholderObjects, groupShapeId) => {
		const placeholders: IPagePlaceholderObjectData[] = [];
		allPagePlaceholderObjectData[groupShapeId] = placeholders;

		_.each(placeholderObjects, (pageObject) => {
			if (pageObject?.attributeKey) {
				let placeholderContent;

				const {
					attributeKey,
					spPr,
					txBody,
				} = pageObject;

				const pictureFill = spPr.fill as IPictureFillProperties;

				if (pictureFill?.blip) {
					placeholderContent = pictureFill.blip.link;
				} else {
					placeholderContent = txBody?.content || "";
					const el = document.createElement("div");
					el.innerHTML = placeholderContent;

					// check for rich text
					const key = new Key({ dpkey: attributeKey });
					const spec = specBuilder.getSpecForKey(key);
					const format = spec?.format;
					if (format === types.FORMAT_RICH) {
						const richTextEl = getPlaceholderRichTextElement(el);
						if (richTextEl) {
							placeholderContent = html.sanitize(richTextEl.innerHTML);
						}
					} else if (format === types.FORMAT_TEXTAREA) {
						el.innerHTML = placeholderContent.replaceAll(PLACEHOLDER_MULTILINE_REGEX, "\n");
						const { innerText } = (el.querySelector(`#${PLACEHOLDER_VALUE_SPAN}`) as HTMLElement);
						// clip first \n off if exists
						placeholderContent = innerText.slice(0, 1) === "\n" ? innerText.slice(1) : innerText;
					} else {
						placeholderContent = "";
						const spans = el.querySelectorAll("span");
						_.each(spans, (span) => {
							// don't get text from the disabled id, if it exists
							if (span.id === PLACEHOLDER_VALUE_SPAN) {
								placeholderContent = span.innerText;
							}
						});
					}
				}
				// Need to use shapeId as there could be duplicates with different nodes
				allPagePlaceholderObjectData[groupShapeId].push({
					attributeKey,
					imagePlaceholder: pageObject.imagePlaceholder || pageObject.isPicture,
					noPermissionToFill: pageObject.noPermissionToFill,
					nodeURL: pageObject.nodeURL,
					value: placeholderContent,
				});
			}
		});
	});

	return allPagePlaceholderObjectData;
};

/**
 * Return all placeholder objects on a canvas page, organized by their containing group shape id.
 * @param {IPage} pageData - optional page data from which to get placeholders, if absent get from current page
 */
const _getAllPagePlaceholderObjects = (pageData: IPage): { [groupShapeId: string]: IGroupShape[] } => {
	const state = getStore()
		.getState() as ICanvasEditorState;
	const page = pageData ? [pageData] : state.pages.present;

	const allPagePlaceholderObjects: { [groupShapeId: string]: IGroupShape[] } = {};

	if (page && page.length && page[0].pageObjects) {
		// get all placeholders from this page as to only get groupShapeId from those
		// this keeps nested group shapes' Id from getting into allPagePlaceholderObjects
		const placeholders = _getPagePlaceholders(page[0].pageObjects, []);
		placeholders.forEach((placeholder: any) => {
			// Put the placeholder objects in an array for the containing group shape id
			allPagePlaceholderObjects[placeholder.shapeId] = getPagePlaceholderObjects(placeholder, []);
		});
	}

	return allPagePlaceholderObjects;
};

const _getAllNonAttributeImagesHelper = (pageObjects: PageObjectType[], imageURLs: String[]) => {
	_.each(pageObjects, (po) => {
		const pageObject = po as IShape;
		if (pageObject.isPicture && !pageObject.attributeKey) {
			let fillString = (pageObject.spPr.fill as IPictureFillProperties).blip.link;
			const rx = /URL=.*?&/g;
			const match = fillString.match(rx);
			if (match && match.length >= 1) {
				fillString = match[0];
			}
			fillString = fillString.substring(4, fillString.length - 1);
			imageURLs.push(fillString);
		} else if ((po as IGroupShape).childObjects) {
			_getAllNonAttributeImagesHelper((po as IGroupShape).childObjects, imageURLs);
		}
	});
};

/**
 * Return all placeholder objects on a canvas page, organized by their containing group shape id.
 * @param {IPage} pageData - optional page data from which to get placeholders, if absent get from current page
 * @returns {String[]} all image URL's from images on the page that are not in placeholders
 */
const _getAllNonAttributeImages = (pageData: IPage) => {
	const state = getStore()
		.getState() as ICanvasEditorState;
	const page = pageData ? [pageData] : state.pages.present;

	const imageURLs: String[] = [];

	_getAllNonAttributeImagesHelper(page[0].pageObjects, imageURLs);

	return imageURLs;
};

/**
 * Make an image from the given placeholder
 * @param object image placeholder to create image from
 * @param image image to be placed in placeholder
 * @returns image object.
 */
const _makeImageFromPlaceholder = (object: IShape, image: IAddImageRequest) => {
	const objWidth = object.spPr.xfrm.ext.cx;
	const objHeight = object.spPr.xfrm.ext.cy;

	let imgWidth = image.width;
	let imgHeight = image.height;

	// Scale image down as much as possible
	if (imgWidth > objWidth || imgHeight > objHeight) {
		const factorW = objWidth / imgWidth;
		const factorH = objHeight / imgHeight;

		const factor = Math.min(factorW, factorH);

		imgWidth = imgWidth * factor;
		imgHeight = imgHeight * factor;
	}

	const xDiffRatio = (objWidth - imgWidth) * PERCENTAGE_HUNDRED / objWidth;
	const yDiffRatio = (objHeight - imgHeight) * PERCENTAGE_HUNDRED / objHeight;

	return {
		attributeKey: object.attributeKey,
		isPicture: true,
		isPictureFromPlaceholder: true,
		noChangeAspect: false,
		nodeURL: object.nodeURL,
		shapeId: generateUUID(),
		spPr: {
			fill: {
				blip: {
					link: image.imageLink,
				},
				srcRect: {
					b: yDiffRatio / 2,
					flipH: false,
					l: xDiffRatio / 2,
					r: xDiffRatio / 2,
					refExt: {
						cx: objWidth,
						cy: objHeight,
					},
					rot: object.spPr.xfrm.rot,
					t: yDiffRatio / 2,
				},
				type: FillTypes.PICTURE_FILL,
			},
			geometry: object.spPr.geometry,
			ln: {
				W: 0,
				fill: {
					color: "none",
					type: FillTypes.SOLID_FILL,
				},
			},
			xfrm: {
				ext: { cx: objWidth, cy: objHeight },
				flipH: object.spPr.xfrm.flipH,
				flipV: object.spPr.xfrm.flipV,
				off: {
					x: object.spPr.xfrm.off.x,
					y: object.spPr.xfrm.off.y,
				},
				rot: object.spPr.xfrm.rot,
			},
		},
		type: IPageObjectTypes.SHAPE,
	} as IShape;
};

/**
 * Get placeholder text format value
 */
export const getPanningMode = () => {
	const state = getStore()
		.getState() as ICanvasEditorState;
	return state.canvasEditor.isPanning;
};

export default {

	/**
	 * Expand editor according to client rectangle
	 */
	expandEditor: _expandEditor,

	/**
	 * Return map of placeholder keys for objects having attributeKey property.
	 * @return contains map of placeholder keys to attribute keys for page objects.
	 */
	extractPagePlaceholderKeys: _extractPagePlaceholderKeys,

	/**
	 * Return all image URL's on the page that are not part of attribute placeholders
	 */
	getAllNonAttributeImages: _getAllNonAttributeImages,

	/**
	 * Return data for all placeholder objects on the current canvas page.
	 */
	getAllPagePlaceholderObjectData: _getAllPagePlaceholderObjectData,

	/**
	 * Return all placeholder objects on the current canvas page.
	 */
	getAllPagePlaceholderObjects: _getAllPagePlaceholderObjects,

	/**
	 * Return canvas container
	 */
	getCanvasContainerEl,

	/** Get canvas container scroll position */
	getCanvasContainerScrollPosition,

	/**
	 * Return current canvas mode.
	 * @returns CanvasMode
	 */
	getCanvasMode: _getCanvasMode,

	/**
	 * Return default canvas mode.
	 * @returns CanvasMode
	 */
	getDefaultCanvasMode: _getDefaultCanvasMode,

	/**
	 * Get Editor dimensions according to content
	 */
	getEditorDimensions: _getEditorDimensions,

	/**
	 * Get all page placeholder objects, including objects in groups and sub-groups.
	 */
	getPagePlaceholderObjects,

	/**
	 * Get panning mode
	 */
	getPanningMode,

	/**
	 * Get placeholder text format value
	 */
	getPlaceholderTextFormat,

	/** Get valid page objects */
	getValidPageObjects,

	/**
	 * Return whether or not canvas mode is currently the default edit mode.
	 * @returns boolean
	 */
	isDefaultCanvasEditMode,

	/**
	 * Return whether or not canvas mode is currently the default.
	 * @returns boolean
	 */
	isDefaultCanvasMode: _isDefaultCanvasMode,

	/**
	 * Return whether or not canvas mode is currently DRAG_TYPE_INSTANCE.
	 * @returns boolean
	 */
	isDragTypeInstanceMode,

	/**
	 * Return whether or not canvas mode is currently DRAW.
	 * @returns boolean
	 */
	isDrawCanvasMode: _isDrawCanvasMode,

	/**
	 * Return whether or not canvas mode is currently EDIT_CURVE.
	 * @returns boolean
	 */
	isEditCurveCanvasMode: _isEditCurveCanvasMode,

	/**
	 * Return whether or not canvas mode is currently EDIT_PICTURE.
	 * @returns boolean
	 */
	isEditPictureCanvasMode: _isEditPictureCanvasMode,

	/** Return whether or not canvas mode is currently EDIT_PEN.
	 * @returns boolean
	 */
	isEditPenMode: _isEditPenMode,

	/**
	 * Return whether or not canvas mode is currently EDIT_TEXT.
	 * @returns boolean
	 */
	isEditTextCanvasMode: _isEditTextCanvasMode,

	/**
	 * Return whether or not canvas mode is currently NODRAW.
	 * @returns boolean
	 */
	isNoDrawCanvasMode: _isNoDrawCanvasMode,

	/**
	 * Return whether or not canvas mode is currently SELECT.
	 * @returns boolean
	 */
	isSelectCanvasMode: _isSelectCanvasMode,

	/**
	 * Make an image from the given placeholder
	 * @returns {IPageObject} image object.
	 */
	makeImageFromPlaceholder: _makeImageFromPlaceholder,

	/* Remove placeholders matching keys from the set of page objects
	 * @return page objects
	 */
	removeImpossibleKeys: _removeImpossibleKeys,

	/** Set canvas container scroll position */
	setCanvasContainerScrollPosition,

	/**
	 * Replace placeholder keys with corresponding attribute key strings.
	 */
	setPagePlaceholderKeys,

	/**
	 * Setup placeholders for content created before C8-16890 7.2
	 */
	setupPageContentPlaceholderHTML,

	/** Toggle placeholder labels optionally based on edit mode and value content */
	setupPlaceholderLabelOptionally,

	/**
	 * Remove placeholders matching keys from the set of page objects
	 */
	unlinkAllAttributePlaceholders: _unlinkAllAttributePlaceholders,

	/**
	 * Update all page objects.
	 */
	updatePageObjects: _updatePageObjects,
};
